import React, { memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Chapter, CSVUploadResults } from './types';

interface CSVUploadDialogProps {
  open: boolean;
  chapter: Chapter | null;
  csvFile: File | null;
  uploading: boolean;
  results: CSVUploadResults | null;
  onClose: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDownloadSample: () => void;
  onUpload: () => void;
}

const CSVUploadDialog = memo<CSVUploadDialogProps>(({
  open,
  chapter,
  csvFile,
  uploading,
  results,
  onClose,
  onFileChange,
  onDownloadSample,
  onUpload,
}) => {
  const hasSuccessfulResults = results && results.successful.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Upload Questions from CSV
        {chapter && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Chapter: {chapter.name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>CSV Format Guidelines:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              • Required columns: Question Text, Question Type, Importance
              <br />
              • Question Type: MCQ, Fill in the blanks, Short, Long
              <br />
              • Importance: High, Medium, Low
              <br />
              • For MCQ: Provide at least 2 options (Option A, Option B, etc.) and Correct Answer
              <br />
              • For Fill in the blanks: Provide Correct Answer
              <br />
              • For Short/Long: Optionally provide Sample Answer
              <br />
              • Optional columns: Marks, Difficulty, Estimated Time, Tags, Past Paper References
            </Typography>
          </Alert>

          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={onDownloadSample}
              size="medium"
            >
              Download Question CSV Template
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
              Download a sample file with the correct format and examples
            </Typography>
          </Box>

          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: 'background.default',
              mb: 2,
            }}
          >
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              type="file"
              onChange={onFileChange}
            />
            <label htmlFor="csv-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                size="large"
              >
                Select CSV File
              </Button>
            </label>
            {csvFile && (
              <Typography variant="body2" sx={{ mt: 2 }} color="success.main">
                Selected: {csvFile.name}
              </Typography>
            )}
          </Box>

          {results && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Upload Results
              </Typography>
              
              {results.successful.length > 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Successfully uploaded {results.successful.length} question(s)
                  </Typography>
                </Alert>
              )}

              {results.failed.length > 0 && (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Failed to upload {results.failed.length} question(s)
                    </Typography>
                  </Alert>
                  <Box
                    sx={{
                      maxHeight: 300,
                      overflow: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    {results.failed.map((failure, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          pb: 2,
                          borderBottom: index < results.failed.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="body2" color="error" fontWeight={600}>
                          Row {failure.row}: {failure.error}
                        </Typography>
                        {failure.data && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Question Text: {failure.data['Question Text'] || 'N/A'}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          variant={hasSuccessfulResults ? 'contained' : 'text'}
          color={hasSuccessfulResults ? 'primary' : 'inherit'}
        >
          Close
        </Button>
        {(!results || results.successful.length === 0) && (
          <Button
            variant="contained"
            onClick={onUpload}
            disabled={!csvFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

CSVUploadDialog.displayName = 'CSVUploadDialog';

export default CSVUploadDialog;
